import { CoinInfo } from '@/utils/types';
import { PublicKey } from '@solana/web3.js';
import { create } from 'zustand';
import { BN, IdlAccounts } from '@coral-xyz/anchor';
import { Fungstake } from '@/program/fungstake/fungstake';
import { PublicLaunchpad } from '@/program/memeLaunchpad/public_launchpad';
import { DefaiLaunchpad } from '@/program/defai_launchpad/defai_launchpad';
import { MODE_COIN } from '@/config';
import { LaunchingMode } from '@/components/creatToken/config';

export enum CoinType {
  AGENT = 'agent',
  MEME = 'meme',
  DEFAI = 'defai',
}

export type CoinInfoState = {
  coinType: CoinType;
  isDEFAI: CoinType;
  coin: Partial<CoinInfo>;
  tokenAddress: string;
  refreshStakeCheck: boolean;
  balanceStakeMint: string;
  coinMode: MODE_COIN;
  agentMode: LaunchingMode;
  totalVault: number;
  rewardAmt: number;
  solAmtBuy: number;
  stakeEndTime: number;
  stakeConfig: IdlAccounts<Fungstake>['stakeConfig'];
  stakingVault: IdlAccounts<PublicLaunchpad>['stakingVault'];
  stakeInfo:
    | IdlAccounts<Fungstake>['stakeInfo']
    | IdlAccounts<PublicLaunchpad>['stakeInfo'];
  curveInfo: Partial<
    | IdlAccounts<DefaiLaunchpad>['bondingCurve']
    | IdlAccounts<PublicLaunchpad>['bondingCurve']
  >;
};

export type CoinStateAction = {
  handleSetStakeInfo: (stakeInfo: CoinInfoState['stakeInfo']) => void;
  handleSetStakeConfig: (stakeConfig: CoinInfoState['stakeConfig']) => void;
  handleSetStakingVault: (totalVault: CoinInfoState['stakingVault']) => void;
  handleSetTotalVault: (totalVault: CoinInfoState['totalVault']) => void;
  handleSetStakeEndTime: (stakeEndTime: CoinInfoState['stakeEndTime']) => void;
  handleSetCurveInfo: (coin: CoinInfoState['curveInfo']) => void;
  handleSetCoinInfo: (curveInfo: CoinInfoState['coin']) => void;
  handleSetRefreshCheck: (refresh: CoinInfoState['refreshStakeCheck']) => void;
  handleSetTokenAddress: (tokenAddress: CoinInfoState['tokenAddress']) => void;
  handleSetCoinType: (coinType: CoinInfoState['coinType']) => void;
  handleSetAgentMode: (coinType: CoinInfoState['agentMode']) => void;
  handleSetIsDEFAICoin: (isDEFAI: CoinInfoState['isDEFAI']) => void;
  handleSetCoinMode: (coinMode: CoinInfoState['coinMode']) => void;
  handleSetRewardAmt: (rewardAmt: CoinInfoState['rewardAmt']) => void;
  handleSetSolAmtBuy: (solAmtBuy: CoinInfoState['solAmtBuy']) => void;
  handleSetStakeMintBalance: (
    balance: CoinInfoState['balanceStakeMint'],
  ) => void;
  // handleCoinStateWithKey: <K extends keyof CoinInfoState>(
  //   key: K,
  //   updateValue: CoinInfoState[K]
  // ) => void;
  resetState: () => void;
};

const initialState: CoinInfoState = {
  coinType: null,
  isDEFAI: null,
  coinMode: null,
  coin: {},
  curveInfo: {},
  tokenAddress: '',
  refreshStakeCheck: false,
  balanceStakeMint: '0',
  stakeInfo: null,
  totalVault: 0,
  stakeEndTime: 0,
  stakeConfig: null,
  stakingVault: null,
  rewardAmt: 0,
  solAmtBuy: 0,
  agentMode: null,
};

export type DepositStoreType = CoinInfoState & { actions: CoinStateAction };

const useDetectionStore = create<DepositStoreType>()((set) => ({
  //States
  ...initialState,

  //Actions
  actions: {
    handleSetStakeMintBalance: (balanceStakeMint) => set({ balanceStakeMint }),
    handleSetCoinInfo: (coin) => set({ coin }),
    handleSetCurveInfo: (curveInfo) => set({ curveInfo }),
    handleSetTokenAddress: (tokenAddress) => set({ tokenAddress }),
    handleSetRefreshCheck: (refreshStakeCheck) => set({ refreshStakeCheck }),
    handleSetStakeInfo: (stakeInfo) => set({ stakeInfo }),
    handleSetStakeConfig: (stakeConfig) => set({ stakeConfig }),
    handleSetTotalVault: (totalVault) => set({ totalVault }),
    handleSetStakingVault: (stakingVault) => set({ stakingVault }),
    handleSetStakeEndTime: (stakeEndTime) => set({ stakeEndTime }),
    handleSetCoinType: (coinType) => set({ coinType }),
    handleSetIsDEFAICoin: (isDEFAI) => set({ isDEFAI }),
    handleSetRewardAmt: (rewardAmt) => set({ rewardAmt }),
    handleSetSolAmtBuy: (solAmtBuy) => set({ solAmtBuy }),
    handleSetCoinMode: (coinMode) => set({ coinMode }),
    handleSetAgentMode: (agentMode) => set({ agentMode }),

    resetState: () =>
      set((state) => {
        state.refreshStakeCheck = initialState.refreshStakeCheck;
        state.coin = initialState.coin;
        state.tokenAddress = initialState.tokenAddress;
        state.balanceStakeMint = initialState.balanceStakeMint;
        state.stakeInfo = initialState.stakeInfo;
        state.stakeConfig = initialState.stakeConfig;
        state.stakingVault = initialState.stakingVault;
        state.coinType = null;
        state.isDEFAI = null;
        state.rewardAmt = 0;
        state.solAmtBuy = 0;
        state.agentMode = null;

        return state;
      }),

    // handleCoinStateWithKey: (k, updateValue) =>
    //   set({
    //     [k]: updateValue,
    //   }),
  },
}));

export default useDetectionStore;
